import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { TimePicker } from './TimePicker';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { formatTime } from '../utils/dateTime';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  editable?: boolean;
};

export function AppTimeField({
  label,
  value,
  onChange,
  placeholder = 'Select time',
  error,
  editable = true,
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const displayValue = value.trim() ? formatTime(value.trim()) : '';

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: palette.textSecondary }]}>{label}</Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={
          displayValue ? `Selected ${displayValue}` : 'Opens time picker'
        }
        accessibilityState={{ disabled: !editable }}
        disabled={!editable}
        onPress={() => {
          if (editable) setOpen(true);
        }}
        style={[
          styles.field,
          {
            borderColor: error ? palette.error : palette.border,
            backgroundColor: palette.surface,
          },
        ]}
      >
        <Text
          style={[
            styles.value,
            {
              color: displayValue ? palette.textPrimary : palette.textMuted,
            },
          ]}
        >
          {displayValue || placeholder}
        </Text>
      </Pressable>
      {error ? (
        <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
          {error}
        </Text>
      ) : null}
      <TimePicker
        open={open}
        value={value}
        onClose={() => setOpen(false)}
        onConfirm={next => {
          onChange(next);
          setOpen(false);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  field: {
    minHeight: 50,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    justifyContent: 'center',
  },
  value: {
    ...typography.input,
    paddingVertical: spacing.md,
  },
  error: { ...typography.caption },
});
